// Module: brick-registry
// Role: Maps checkpoint kind + artefact_requirements to brick components.
//       FlowScreen dispatches to bricks via this registry.
// Uses: VideoUploadBrick, ScreenshotBrick, UrlInputBrick
// Used by: FlowScreen.tsx

import type { ComponentType } from 'react';
import type { Checkpoint, ArtefactRequirement } from '../../../api/flow-types';
import type { VideoUploadBrickProps } from './VideoUploadBrick';
import { VideoUploadBrick } from './VideoUploadBrick';
import { ScreenshotBrick } from './ScreenshotBrick';
import { UrlInputBrick } from './UrlInputBrick';

// Common brick props — all bricks share the same base prop shape.
export type BrickProps = VideoUploadBrickProps;

// ArtefactKind -> brick component mapping.
const BRICK_REGISTRY: Record<string, ComponentType<BrickProps>> = {
  video: VideoUploadBrick as ComponentType<BrickProps>,
  screenshot: ScreenshotBrick as ComponentType<BrickProps>,
  url: UrlInputBrick as ComponentType<BrickProps>,
};

export interface BrickEntry {
  /** Stable React key: `${kind}-${index}` (EC-10: duplicate-kind safe). */
  key: string;
  Component: ComponentType<BrickProps>;
  requirement: ArtefactRequirement;
}

/**
 * Resolve the bricks needed for a checkpoint, keyed by (kind,index)
 * to handle duplicate-kind requirements (EC-10).
 */
export function bricksForCheckpoint(checkpoint: Checkpoint): BrickEntry[] {
  const reqs = checkpoint.artefact_requirements;
  if (!reqs || reqs.length === 0) return [];

  const entries: BrickEntry[] = [];
  const kindCounters: Record<string, number> = {};

  for (const req of reqs) {
    const kind = req.kind;
    const idx = kindCounters[kind] ?? 0;
    kindCounters[kind] = idx + 1;

    const Component = BRICK_REGISTRY[kind];
    if (!Component) continue; // unknown kind — skip (InstructionScreen fallback)

    entries.push({
      key: `${kind}-${idx}`,
      Component,
      requirement: req,
    });
  }

  return entries;
}
